import React from 'react';
import { Task, Subtask, TimeEntry } from '../../types/task';
import { useLanguage } from '../../contexts/LanguageContext';
import { useProjects } from '../../hooks/useProjects';
import Modal from '../common/Modal';
import TimeTracker from './TimeTracker';
import TaskComments from './TaskComments';
import SubtaskList from './SubtaskList';
import { PRIORITY_META, toDate, fmtDue, dueState, initials } from './views/taskMeta';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { useAuth } from '../../contexts/AuthContext';
import { startTimer } from '../../services/timeTrackingService';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon, CalendarIcon, ClockIcon, TagIcon, BriefcaseIcon, BuildingOffice2Icon,
  ChatBubbleLeftIcon, ArrowDownTrayIcon, PencilSquareIcon, ArrowTopRightOnSquareIcon, UserIcon
} from '@heroicons/react/24/outline';

interface TaskViewProps {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (task: Task) => void;
  onAddComment?: (message: string, files?: File[]) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  onUpdateComment?: (commentId: string, message: string) => Promise<void>;
  onDeleteAttachment?: (commentId: string, attachmentId: string) => Promise<void>;
  onExportTask?: () => void;
  onUpdateStatus?: (taskId: string, status: Task['status'], statusId?: string) => void;
  onUpdatePriority?: (taskId: string, priority: Task['priority']) => void;
  onUpdateSubtasks?: (taskId: string, subtasks: Subtask[]) => Promise<void> | void;
}

const fmtDateTime = (v: any) => {
  const d = toDate(v);
  return d ? d.toLocaleString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–';
};

const Prop: React.FC<{ label: string; icon?: React.ElementType; children: React.ReactNode }> = ({ label, icon: Icon, children }) => (
  <div className="flex items-start gap-2 py-2 border-b border-[var(--crm-border)] last:border-0">
    <div className="w-24 flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold pt-1">
      {Icon && <Icon className="w-3.5 h-3.5" />}{label}
    </div>
    <div className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{children}</div>
  </div>
);

const TaskView: React.FC<TaskViewProps> = ({
  task, isOpen, onClose, onEdit, onAddComment, onDeleteComment, onUpdateComment, onDeleteAttachment, onExportTask,
  onUpdateStatus, onUpdatePriority, onUpdateSubtasks
}) => {
  const { t } = useLanguage();
  const { projects } = useProjects();
  const { statuses, statusOf } = useTaskStatuses();
  const current = statusOf(task);
  const project = task.projectId ? projects.find(p => p.id === task.projectId) : undefined;
  const pr = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const ds = dueState(task);
  const dueCls = ds === 'overdue' ? 'text-rose-600 dark:text-rose-400' : ds === 'today' ? 'text-amber-600 dark:text-amber-400' : '';
  const subtasks = task.subtasks || [];
  const { currentUser } = useAuth();
  const [timerKey, setTimerKey] = React.useState(0);
  const [runningEntry, setRunningEntry] = React.useState<TimeEntry | null>(null);
  const startSubtaskTimer = async (sub: Subtask) => {
    if (!currentUser) return;
    try {
      await startTimer(task.id, currentUser.uid, currentUser.displayName || currentUser.email || 'Ismeretlen', task.organizationId, sub.title, { id: sub.id, title: sub.title });
      toast.success(`Időmérő elindítva: ${sub.title}`);
      setTimerKey(k => k + 1);
    } catch (e: any) { toast.error(e?.message || 'Az időmérő nem indult el'); }
  };
  const totalHours = (task.actualHours || 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={task.taskNumber} size="6xl" noPadding>
      <div className="flex flex-col min-h-full">
        {/* Fejléc */}
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)]">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {onUpdateStatus ? (
              <div className="inline-flex items-center rounded-lg border border-[var(--crm-border)] p-0.5 flex-wrap">
                {statuses.map(s => (
                  <button key={s.id} type="button" onClick={() => onUpdateStatus(task.id, s.category, s.id)}
                    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${current.id === s.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.id === s.id ? undefined : s.color }} />{s.name}
                  </button>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: current.color }} />{current.name}</span>
            )}
            {onUpdatePriority ? (
              <select value={task.priority} onChange={e => onUpdatePriority(task.id, e.target.value as Task['priority'])} className={`h-7 pl-2 pr-7 text-xs rounded-lg border ${pr.text}`}>
                {(Object.keys(PRIORITY_META) as Task['priority'][]).map(k => <option key={k} value={k}>{PRIORITY_META[k].label}</option>)}
              </select>
            ) : (
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${pr.text}`}><span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} />{pr.label}</span>
            )}
          </div>
          <h2 className={`text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 break-words ${task.status === 'completed' ? 'line-through text-gray-400' : ''}`}>{task.title}</h2>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]">
          {/* Fő oszlop */}
          <div className="p-4 sm:p-6 space-y-6 min-w-0">
            <section>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Leírás</div>
              {task.description ? (
                <div className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{task.description}</div>
              ) : (
                <button type="button" onClick={() => onEdit?.(task)} className="text-sm text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">Nincs leírás – kattints a hozzáadáshoz</button>
              )}
            </section>

            <section>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Alfeladatok{subtasks.length ? ` (${subtasks.filter(s => s.done).length}/${subtasks.length})` : ''}</div>
              <SubtaskList subtasks={subtasks} onChange={(next) => onUpdateSubtasks?.(task.id, next)} disabled={!onUpdateSubtasks} onStartTimer={startSubtaskTimer} runningSubtaskId={runningEntry?.subtaskId || null} />
            </section>

            {task.documents && task.documents.length > 0 && (
              <section>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.documents') || 'Dokumentumok'} ({task.documents.length})</div>
                <ul className="divide-y divide-[var(--crm-border)] rounded-lg border border-[var(--crm-border)]">
                  {task.documents.map((doc, i) => (
                    <li key={doc.id || i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 min-w-0 hover:text-primary-700 dark:hover:text-primary-300" title="Megnyitás új lapon"><DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{doc.originalName || doc.name}</span>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">{doc.size ? `${Math.round(doc.size / 1024)} KB` : ''}</span></a>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-white" title="Megnyitás"><ArrowTopRightOnSquareIcon className="w-4 h-4" /></a>
                        <a href={doc.url} download={doc.originalName || doc.name} className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-white" title="Letöltés"><ArrowDownTrayIcon className="w-4 h-4" /></a>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5 inline-flex items-center gap-1.5"><ClockIcon className="w-3.5 h-3.5" />Időmérés</div>
              <TimeTracker taskId={task.id} organizationId={task.organizationId} refreshKey={timerKey} onRunningChange={setRunningEntry} />
            </section>

            {onAddComment && onDeleteComment && (
              <section>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5 inline-flex items-center gap-1.5"><ChatBubbleLeftIcon className="w-3.5 h-3.5" />{t('tasks.sections.comments') || 'Megjegyzések'} ({task.comments?.length || 0})</div>
                <TaskComments task={task} onAddComment={onAddComment} onDeleteComment={onDeleteComment} onUpdateComment={onUpdateComment} onDeleteAttachment={onDeleteAttachment} />
              </section>
            )}
          </div>

          {/* Tulajdonságok */}
          <aside className="border-t lg:border-t-0 lg:border-l border-[var(--crm-border)] bg-[var(--crm-bg)] p-4 sm:p-5 lg:sticky lg:top-0 lg:self-start">
            <Prop label="Felelős" icon={UserIcon}>
              {task.assignedToName ? (
                <span className="inline-flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold flex items-center justify-center">{initials(task.assignedToName)}</span>{task.assignedToName}</span>
              ) : <span className="text-gray-400">Nincs kiosztva</span>}
            </Prop>
            <Prop label="Határidő" icon={CalendarIcon}>
              {task.dueDate ? <span className={`tabular-nums ${dueCls}`}>{fmtDateTime(task.dueDate)} <span className="text-xs opacity-70">({fmtDue(task.dueDate)})</span></span> : <span className="text-gray-400">–</span>}
            </Prop>
            {task.completedDate && <Prop label="Kész" icon={CalendarIcon}><span className="tabular-nums text-emerald-600 dark:text-emerald-400">{fmtDateTime(task.completedDate)}</span></Prop>}
            <Prop label="Ügyfél" icon={BuildingOffice2Icon}>{task.customerName || <span className="text-gray-400">–</span>}</Prop>
            <Prop label="Projekt" icon={BriefcaseIcon}>{project?.name || <span className="text-gray-400">–</span>}</Prop>
            <Prop label="Órák" icon={ClockIcon}>
              <div className="text-sm tabular-nums">{totalHours.toFixed(1)} ó{task.estimatedHours ? <span className="text-gray-400"> / becsült {task.estimatedHours} ó</span> : null}</div>
              {task.estimatedHours ? (
                <div className="mt-1 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className={`h-full rounded-full ${totalHours > task.estimatedHours ? 'bg-rose-500' : 'bg-primary-600'}`} style={{ width: `${Math.min(100, Math.round((totalHours / task.estimatedHours) * 100))}%` }} /></div>
              ) : null}
            </Prop>
            <Prop label="Címkék" icon={TagIcon}>
              {task.tags && task.tags.length ? <div className="flex flex-wrap gap-1">{task.tags.map(tag => <span key={tag} className="px-2 h-6 inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs">{tag}</span>)}</div> : <span className="text-gray-400">–</span>}
            </Prop>
            {task.customFields && Object.keys(task.customFields).length > 0 && (
              <Prop label="Egyedi">
                <dl className="space-y-0.5 text-xs">{Object.entries(task.customFields).map(([k, v]) => <div key={k} className="flex justify-between gap-2"><dt className="text-gray-500">{k}</dt><dd className="truncate">{String(v ?? '–')}</dd></div>)}</dl>
              </Prop>
            )}
            <div className="pt-3 text-[11px] text-gray-400 dark:text-gray-500 space-y-0.5">
              <div>Létrehozta: {task.createdByName || '–'} · {fmtDateTime(task.createdAt)}</div>
              <div>Módosítva: {fmtDateTime(task.updatedAt)}</div>
            </div>
          </aside>
        </div>

        {/* Műveletsáv */}
        <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto">Bezárás</button>
          {onExportTask && <button type="button" onClick={onExportTask} className="crm-btn crm-btn-secondary"><ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />Export</button>}
          {onEdit && <button type="button" onClick={() => { onEdit(task); onClose(); }} className="crm-btn crm-btn-primary"><PencilSquareIcon className="w-4 h-4 mr-1.5" />{t('common.edit') || 'Szerkesztés'}</button>}
        </div>
      </div>
    </Modal>
  );
};

export default TaskView;
